
import React from 'react';
import Header from '@/components/common/Header';
import SEOHead from '@/components/common/SEOHead';
import FooterSection from '@/components/homepage/FooterSection';
import { useAnalytics } from '@/hooks/useAnalytics';

const PrivacyPolicy = () => {
  const { trackNavigation } = useAnalytics(); // This also handles page view tracking
  
  return (
    <div className="min-h-screen bg-[#F7F5F2] font-nunito">
      <SEOHead pageKey="privacy" />
      <Header showActions={true} />
      
      <main className="pt-[80px]">
        {/* Header */}
        <div className="section-container py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-[32px] md:text-[72px] font-bold text-[#6C4F77] mb-4 leading-tight">Privacy Policy / Datenschutzerklärung</h1>
            <p className="text-[#2C2C2C] text-lg">
              Last updated: June 8, 2025
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white py-12 mx-[20px] md:mx-0 rounded-[8px] md:rounded-none">
          <div className="section-container">
            <div className="max-w-4xl mx-auto space-y-8">
            <section>
              <p className="text-[#2C2C2C] leading-relaxed">
                Welcome to Wabisabi ("we," "our," or "us"). We believe relationships are worth remembering — and that includes respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal data when you interact with our website, app, and services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">1. What We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Information You Provide</h3>
                  <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                    <li><strong>Waitlist Sign-Up:</strong> Email address</li>
                    <li><strong>Account Info:</strong> Name, email, and profile details (if you create one)</li>
                    <li><strong>Contact Data:</strong> Information you enter about your relationships — like names, notes, birthdays, preferences</li>
                    <li><strong>User Content:</strong> Notes, memories, photos, or reminders you save</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Information Collected Automatically</h3>
                  <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                    <li><strong>Usage Data:</strong> Page views, time spent, and interaction flow</li>
                    <li><strong>Device Info:</strong> Browser, OS, screen size</li>
                    <li><strong>Log Data:</strong> IP address and timestamps (for security and debugging)</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">2. How We Use Your Data</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">We use your data to:</p>
              <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                <li>Provide, personalize, and improve Wabisabi</li>
                <li>Send thoughtful reminders and suggestions</li>
                <li>Notify you of launches, updates, or features</li>
                <li>Offer customer support</li>
                <li>Analyze usage to enhance your experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">3. Legal Basis for Processing</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">We process your data under the following legal bases, as defined by the GDPR:</p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium text-[#2C2C2C]">Purpose</th>
                      <th className="text-left py-2 font-medium text-[#2C2C2C]">Legal Basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 pr-4 text-[#2C2C2C]">Email sign-up & communication</td>
                      <td className="py-2 text-[#2C2C2C]">Consent (Art. 6(1)(a) GDPR)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4 text-[#2C2C2C]">Analytics, UX improvement</td>
                      <td className="py-2 text-[#2C2C2C]">Legitimate Interest (Art. 6(1)(f))</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 text-[#2C2C2C]">Legal compliance</td>
                      <td className="py-2 text-[#2C2C2C]">Legal Obligation (Art. 6(1)(c))</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[#2C2C2C] leading-relaxed mt-4">You may withdraw your consent at any time.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">4. Who We Share Data With</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                We do not sell or trade your personal information.
              </p>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">We may share limited data with:</p>
              <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                <li><strong>Service Providers:</strong> To help us run Wabisabi (e.g., hosting, email marketing, analytics)</li>
                <li><strong>Legal Authorities:</strong> If required by law</li>
                <li><strong>Business Transfers:</strong> In case of a merger or acquisition</li>
                <li><strong>With Your Consent:</strong> For optional features</li>
              </ul>
              <div className="mt-4 p-4 bg-[#F7F5F2] rounded-lg">
                <h4 className="font-medium text-[#2C2C2C] mb-2">Current Tools We Use:</h4>
                <ul className="list-disc list-inside text-[#2C2C2C] space-y-1 ml-4">
                  <li>Vercel (hosting, US)</li>
                  <li>Supabase (database, US)</li>
                  <li>EmailOctopus (email communication, UK)</li>
                  <li>Google Analytics (website analytics, US)</li>
                </ul>
                <p className="text-[#2C2C2C] text-sm mt-2">Some providers may process data outside the EU/EEA. We ensure appropriate safeguards, such as Standard Contractual Clauses, are in place.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">5. Cookies & Tracking</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                We use cookies and similar technologies for analytics and experience improvement.
              </p>
              <p className="text-[#2C2C2C] leading-relaxed">
                You can manage your cookie preferences via the [Cookie Settings] link in our footer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">6. Data Security</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                We use modern encryption, secure hosting, and best practices to protect your data. While no method is 100% secure, we commit to protecting your trust.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">7. Your Rights</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                <li>Access your data (Art. 15 GDPR)</li>
                <li>Correct inaccurate info (Art. 16 GDPR)</li>
                <li>Delete your data (Art. 17 GDPR)</li>
                <li>Restrict or object to processing (Art. 18, 21 GDPR)</li>
                <li>Export your data (Art. 20 GDPR)</li>
                <li>Withdraw consent at any time (Art. 7(3) GDPR)</li>
              </ul>
              <p className="text-[#2C2C2C] leading-relaxed mt-4">
                To request any of these, email us at: <a href="mailto:hello@appwabisabi.com" className="text-[#2C2C2C] hover:underline">hello@appwabisabi.com</a>
              </p>
              <div className="mt-4 p-4 bg-[#F7F5F2] rounded-lg">
                <h4 className="font-medium text-[#2C2C2C] mb-2">Supervisory Authority</h4>
                <p className="text-[#2C2C2C] text-sm">
                  You may also file a complaint with your local authority. In Berlin:
                </p>
                <blockquote className="mt-2 pl-4 border-l-2 border-[#2C2C2C] text-sm text-[#2C2C2C]">
                  Berliner Beauftragte für Datenschutz und Informationsfreiheit<br />
                  <a href="https://www.datenschutz-berlin.de" className="text-[#2C2C2C] hover:underline">https://www.datenschutz-berlin.de</a>
                </blockquote>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">8. Data Retention</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                We retain your data as long as your account is active or required for our services. Once deleted, data is fully removed unless required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">9. International Transfers</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                Your data may be stored or processed in the EU, US, or other countries. When outside the EEA, we use legal mechanisms like Standard Contractual Clauses to safeguard your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">10. Children's Privacy</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                Wabisabi is not intended for children under 13. If we learn we've collected data from a child under 13, we'll delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">11. Changes to This Policy</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. Significant changes will be announced via email or posted prominently on our site.
              </p>
              <p className="text-[#2C2C2C] leading-relaxed">
                <strong>Current version:</strong> June 8, 2025
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">12. Contact Us</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                If you have questions about privacy, or just want to talk about memory and connection, reach out anytime:
              </p>
              <div className="mt-4 p-4 bg-[#F7F5F2] rounded-lg">
                <p className="text-[#2C2C2C]">
                  <strong>Email:</strong> <a href="mailto:hello@appwabisabi.com" className="text-[#2C2C2C] hover:underline">hello@appwabisabi.com</a><br />
                  <strong>Address:</strong> Bismarck Strasse 87, 10627 Berlin, Germany
                </p>
              </div>
            </section>
            </div>
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
};

export default PrivacyPolicy;
